import * as React from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // Carousel styles

import { UnscrollableContainer } from '../../misc';
import {
  Container,
  ColumnOne,
  ColumnTwo,
  ColumnOneContent,
  ContentTitle,
  ContentSubTitle,
  ContentCTOs,
  ImageOne,
  ImageTwo,
  ImageThree,
  MobileViewCarousel,
  LoginCTO,
} from './styles';
import stdImgOne from '../../assets/std1.jpeg';
import stdImgTwo from '../../assets/std2.webp';
import stdImgThree from '../../assets/std3.jpg';
import { Modal, PageHeader } from '../../components';
import { InvigilatorLoginForm, StudentLoginForm } from './components';
import { Helmet } from 'react-helmet';

/**
 * LandingPage
 *
 * Main landing page for the app.
 * - Allows users to choose between Student and Invigilator login.
 * - Shows a carousel of images on mobile and a static image grid on desktop.
 * - Displays a modal with the appropriate login form.
 */
export const LandingPage = (): JSX.Element => {
  // State to control modal visibility
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  // State to track which user type is logging in
  const [user, setUser] = React.useState<string>('');

  /**
   * Toggles the login modal open/closed.
   */
  const toggleModal = (): void => {
    setIsModalOpen(!isModalOpen);
  };

  return (
    <UnscrollableContainer>
      {/* Set page title and meta info */}
      <Helmet>
        <title>KNUST, CoE Check timetable</title>
        <meta
          name="check-timetable"
          content="Sign in page for KNUST,college of engineering students and 
          invigilators to check timetable schedule"
        />
      </Helmet>
      <Container>
        {/* Left column: main content and login options */}
        <ColumnOne>
          <PageHeader />
          {/* Mobile-only carousel of student images */}
          <MobileViewCarousel>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <Carousel
              showArrows={false}
              showThumbs={false}
              autoPlay
              infiniteLoop
              showStatus={false}
            >
              {/* Using ImageOne styles for all the carousel images */}
              <div>
                <ImageOne src={stdImgThree} />
              </div>
              <div>
                <ImageOne src={stdImgTwo} />
              </div>
              <div>
                <ImageOne src={stdImgOne} />
              </div>
            </Carousel>
          </MobileViewCarousel>
          {/* Main content: title, subtitle, and login buttons */}
          <ColumnOneContent>
            <ContentTitle style={{ wordSpacing: '10px' }}>How prepared are you?</ContentTitle>
            <ContentSubTitle>Please log in to access your timetable</ContentSubTitle>
            <ContentCTOs>
              {/* Student login button */}
              <LoginCTO
                onClick={() => {
                  toggleModal();
                  setUser('student');
                  localStorage.setItem('coe-current-user', 'student');
                }}
              >
                <span>Student login</span> <i className="ml-2 mt-1 fas fa-angle-right" />
              </LoginCTO>
              {/* Invigilator login button */}
              <LoginCTO
                onClick={() => {
                  toggleModal();
                  setUser('invigilator');
                  localStorage.setItem('coe-current-user', 'invigilator');
                }}
              >
                <span>Invigilator login</span> <i className="ml-2 mt-1 fas fa-angle-right" />
              </LoginCTO>
            </ContentCTOs>
          </ColumnOneContent>
        </ColumnOne>
        {/* Right column: static image grid, only visible on large screens */}
        <div className="lg:w-1/2 hidden lg:flex lg:justify-evenly pt-12 pl-24 pr-10 flex-col relative bg-[#1A73E3]">
          {/* First image */}
          <div className="rounded-lg w-96 overflow-hidden">
            <img
              src={stdImgOne}
              alt="student image one"
              className="block max-w-full object-center"
            />
          </div>
          {/* Second image, aligned to the right */}
          <div className="rounded-lg w-96 overflow-hidden ml-auto">
            <img
              src={stdImgTwo}
              alt="student image two"
              className="block max-w-full object-center"
            />
          </div>
          {/* Third image */}
          <div className="rounded-lg w-96 overflow-hidden">
            <img
              src={stdImgThree}
              alt="student image three"
              className="block max-w-full object-center"
            />
          </div>
          {/* Alternative: styled image components (commented out) */}
          {/* <ImageTwo src={stdImgOne} />
          <ImageOne src={stdImgTwo} />
          <ImageThree src={stdImgThree} /> */}
        </div>
      </Container>
      {/* Login modal: shows either the student or invigilator login form */}
      <Modal modalIsOpen={isModalOpen} toggleModal={toggleModal}>
        {user === 'student' ? (
          <StudentLoginForm toggleModal={toggleModal} />
        ) : (
          <InvigilatorLoginForm toggleModal={toggleModal} />
        )}
      </Modal>
    </UnscrollableContainer>
  );
};
